import React, { useState, useEffect, useCallback } from 'react';
import { leaveApplicationAPI, leaveBalanceAPI, leaveTypeAPI, uploadAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Calendar,
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Pencil,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPE_COLORS = {
  'Casual Leave': 'bg-blue-100 text-blue-700',
  'Sick Leave': 'bg-red-100 text-red-700',
  'Earned/Privilege Leave': 'bg-green-100 text-green-700',
  'Comp-off': 'bg-purple-100 text-purple-700',
  'Unpaid Leave': 'bg-slate-100 text-slate-700',
  'Maternity Leave': 'bg-pink-100 text-pink-700',
  'Paternity Leave': 'bg-cyan-100 text-cyan-700',
};

const EmployeeLeave = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Apply modal
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [editingAppId, setEditingAppId] = useState(null);
  const [applyData, setApplyData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    is_half_day: false,
    half_day_type: 'morning',
    reason: '',
    document_url: '',
  });
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [selectedBalance, setSelectedBalance] = useState(null);
  const [noBalanceWarning, setNoBalanceWarning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await leaveTypeAPI.getAll();
      setLeaveTypes(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  }, []);

  const fetchBalances = useCallback(async () => {
    try {
      const response = await leaveBalanceAPI.getMy();
      setBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await leaveApplicationAPI.getMy();
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveTypes();
    fetchBalances();
    fetchApplications();
  }, [fetchLeaveTypes, fetchBalances, fetchApplications]);

  // Calculate working days when dates change
  useEffect(() => {
    if (applyData.start_date && applyData.end_date) {
      if (applyData.is_half_day) {
        setCalculatedDays(0.5);
      } else {
        // All days are working days (no weekend exclusions)
        const start = new Date(applyData.start_date);
        const end = new Date(applyData.end_date);
        let days = 0;
        const current = new Date(start);
        while (current <= end) {
          days++;
          current.setDate(current.getDate() + 1);
        }
        setCalculatedDays(days);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [applyData.start_date, applyData.end_date, applyData.is_half_day]);

  // Check balance warning
  useEffect(() => {
    if (applyData.leave_type_id && calculatedDays > 0) {
      const balance = balances.find(b => b.leave_type_id === applyData.leave_type_id);
      setSelectedBalance(balance);
      if (balance && balance.balance < calculatedDays) {
        const leaveType = leaveTypes.find(t => t.id === applyData.leave_type_id);
        setNoBalanceWarning(leaveType?.name !== 'Unpaid Leave');
      } else {
        setNoBalanceWarning(false);
      }
    } else {
      setSelectedBalance(null);
      setNoBalanceWarning(false);
    }
  }, [applyData.leave_type_id, calculatedDays, balances, leaveTypes]);

  const openApplyModal = () => {
    setEditingAppId(null);
    setApplyData({
      leave_type_id: '',
      start_date: '',
      end_date: '',
      is_half_day: false,
      half_day_type: 'morning',
      reason: '',
      document_url: '',
    });
    setIsApplyModalOpen(true);
  };

  const openEditModal = (app) => {
    setEditingAppId(app.id);
    setApplyData({
      leave_type_id: app.leave_type_id,
      start_date: app.start_date,
      end_date: app.end_date,
      is_half_day: app.is_half_day || false,
      half_day_type: app.half_day_type || 'morning',
      reason: app.reason,
      document_url: app.document_url || '',
    });
    setIsApplyModalOpen(true);
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Withdraw this leave application? This cannot be undone.')) return;
    try {
      await leaveApplicationAPI.delete(appId);
      await fetchApplications();
      await fetchBalances();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadDocument(file);
      setApplyData(prev => ({ ...prev, document_url: response.data.url }));
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async () => {
    setSubmitting(true);
    try {
      const submitData = {
        ...applyData,
        document_url: applyData.document_url || null,
      };
      
      // If half day, set end_date = start_date
      if (applyData.is_half_day) {
        submitData.end_date = applyData.start_date;
      }
      
      if (editingAppId) {
        await leaveApplicationAPI.edit(editingAppId, submitData);
      } else {
        await leaveApplicationAPI.apply(submitData);
      }
      await fetchApplications();
      await fetchBalances();
      setIsApplyModalOpen(false);
      setEditingAppId(null);
    } catch (error) {
      console.error('Failed to apply for leave:', error);
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const getLeaveTypeColor = (typeName) => {
    return LEAVE_TYPE_COLORS[typeName] || 'bg-slate-100 text-slate-700';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const getDocUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="employee-leave-page">
      <button onClick={() => navigate('/portal')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1D35A0] transition-colors mb-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </button>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">My Leave</h2>
          <p className="text-slate-500 text-sm mt-1">
            Apply for leave and view your leave history
          </p>
        </div>
        <Button
          onClick={openApplyModal}
          className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
          data-testid="apply-leave-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Apply for Leave
        </Button>
      </div>

      {/* Leave Balances */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="leave-balances">
        {balances.map((balance) => (
          <div
            key={balance.id}
            className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
            data-testid={`balance-${balance.leave_type_id}`}
          >
            <p className="text-xs text-slate-500 truncate" title={balance.leave_type_name}>
              {balance.leave_type_name}
            </p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`text-2xl font-bold ${balance.balance <= 0 ? 'text-red-600' : 'text-[#1D35A0]'}`}>
                {balance.balance}
              </span>
              <span className="text-sm text-slate-400">/ {balance.entitlement}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Leave History */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-[#1D35A0]">Leave History</h3>
        </div>
        
        {applications.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>No leave applications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {applications.map((app) => (
              <div key={app.id} className="p-4" data-testid={`application-${app.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLeaveTypeColor(app.leave_type_name)}`}>
                        {app.leave_type_name}
                      </span>
                      {getStatusBadge(app.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Dates</p>
                        <p className="font-medium">
                          {app.start_date === app.end_date 
                            ? app.start_date 
                            : `${app.start_date} to ${app.end_date}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Days</p>
                        <p className="font-medium">
                          {app.working_days} {app.is_half_day ? `(${app.half_day_type})` : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Applied</p>
                        <p className="font-medium">
                          {new Date(app.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {app.approved_by_name && (
                        <div>
                          <p className="text-slate-500">{app.status === 'approved' ? 'Approved by' : 'Reviewed by'}</p>
                          <p className="font-medium">{app.approved_by_name}</p>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 mt-2">
                      <span className="text-slate-500">Reason:</span> {app.reason}
                    </p>
                    
                    {app.admin_comment && (
                      <p className="text-sm mt-2 p-2 bg-slate-50 rounded">
                        <span className="text-slate-500">Admin note:</span> {app.admin_comment}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2">
                      {app.document_url && (
                        <a
                          href={getDocUrl(app.document_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <FileText className="w-4 h-4" />
                          View Document
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {app.status === 'pending' && (
                        <div className="flex items-center gap-1 ml-auto">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => openEditModal(app)}
                            data-testid={`edit-leave-${app.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteApplication(app.id)}
                            data-testid={`delete-leave-${app.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Withdraw
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply Leave Modal */}
      <Dialog open={isApplyModalOpen} onOpenChange={setIsApplyModalOpen}>
        <DialogContent className="max-w-lg" data-testid="apply-leave-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              {editingAppId ? 'Edit Leave Application' : 'Apply for Leave'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {editingAppId ? 'Update your leave application details' : 'Fill in the details for your leave application'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Leave Type */}
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={applyData.leave_type_id}
                onValueChange={(value) => setApplyData(prev => ({ ...prev, leave_type_id: value }))}
              >
                <SelectTrigger data-testid="select-leave-type">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => {
                    const balance = balances.find(b => b.leave_type_id === type.id);
                    return (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({balance?.balance || 0} days)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <DatePicker
                  value={applyData.start_date}
                  onChange={(v) => setApplyData(prev => ({ ...prev, start_date: v }))}
                  placeholder="Start date"
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <DatePicker
                  value={applyData.end_date}
                  onChange={(v) => setApplyData(prev => ({ ...prev, end_date: v }))}
                  placeholder="End date"
                  data-testid="input-end-date"
                />
              </div>
            </div>

            {/* Half Day Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_half_day"
                  checked={applyData.is_half_day}
                  onCheckedChange={(checked) => {
                    setApplyData(prev => ({ 
                      ...prev, 
                      is_half_day: checked,
                      end_date: checked ? prev.start_date : prev.end_date
                    }));
                  }}
                  data-testid="checkbox-half-day"
                />
                <Label htmlFor="is_half_day" className="text-sm font-normal cursor-pointer">
                  Half Day Leave
                </Label>
              </div>
              
              {applyData.is_half_day && (
                <Select
                  value={applyData.half_day_type}
                  onValueChange={(value) => setApplyData(prev => ({ ...prev, half_day_type: value }))}
                >
                  <SelectTrigger className="w-36" data-testid="select-half-day-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning</SelectItem>
                    <SelectItem value="afternoon">Afternoon</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Calculated Days & Balance */}
            {calculatedDays > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Working days:</span>
                  <span className="font-semibold">{calculatedDays}</span>
                </div>
                {selectedBalance && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-600">Balance after approval:</span>
                    <span className={`font-semibold ${selectedBalance.balance - calculatedDays < 0 ? 'text-red-600' : ''}`}>
                      {selectedBalance.balance - calculatedDays}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* No Balance Warning */}
            {noBalanceWarning && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Insufficient Balance</p>
                  <p>You don't have enough balance. This will be recorded as Unpaid Leave.</p>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={applyData.reason}
                onChange={(e) => setApplyData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Enter the reason for your leave"
                rows={3}
                data-testid="input-reason"
              />
            </div>

            {/* Document Upload */}
            <div className="space-y-2">
              <Label>Supporting Document (optional)</Label>
              <div className="flex items-center gap-2">
                <Label className="cursor-pointer flex-1">
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors justify-center">
                    <Upload className="w-4 h-4" />
                    <span>{uploading ? 'Uploading...' : 'Upload Document'}</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    disabled={uploading}
                    data-testid="document-upload-input"
                  />
                </Label>
                {applyData.document_url && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Uploaded
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsApplyModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={
                  submitting ||
                  !applyData.leave_type_id ||
                  !applyData.start_date ||
                  (!applyData.is_half_day && !applyData.end_date) ||
                  !applyData.reason
                }
                data-testid="submit-leave-btn"
              >
                {submitting ? 'Submitting...' : editingAppId ? 'Update Application' : 'Submit Application'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeLeave;
